import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Compass,
  ShieldCheck,
  Building2,
  Layers,
  Sparkles,
  FileText,
  Clock,
  ArrowUpFromLine,
  Maximize2,
  HardHat,
  Truck
} from 'lucide-react';
import { COMPANY_INFO, STAFF } from '../data';
import ContactForm from '../components/ContactForm';

export default function Liftudlejning() {
  const getOptimizedUrl = (url, width = 1200) => {
    if (url && url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  const heroImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/jlg-bomlift-udlejning.png";
  const scissorImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/sakselift-arbejdsplatform-udlejning.jpg";
  const boomImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/gallery-boom-lift.jpg";
  const craneImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minikran-loeft-materiel-udlejning.jpg";
  const minilaesserImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minilaesser-laessemaskine-udlejning.jpg";

  const catalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/";
  const bomlifteCatalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=93";
  const sakselifteCatalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=25";
  const kranerCatalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=306";

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Liftudlejning | Vesthimmerlands Materieludlejning</title>
        <meta
          name="description"
          content="Lej bomlifte og sakselifte til arbejde i højden hos Vesthimmerlands Materieludlejning i Svenstrup. Få hurtig rådgivning og et godt tilbud."
        />
        <meta property="og:title" content="Liftudlejning | Vesthimmerlands Materieludlejning" />
        <meta
          property="og:description"
          content="Lej bomlifte og sakselifte til arbejde i højden hos Vesthimmerlands Materieludlejning i Svenstrup. Få hurtig rådgivning og et godt tilbud."
        />
        <meta property="og:image" content={heroImage} />
        <link rel="canonical" href="https://vhmu.dk/liftudlejning" />
      </Helmet>

      {/* Hero Header Sektion */}
      <section className="relative overflow-hidden bg-brand-light border-b border-brand-grey pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {/* Brødkrumme */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-6">
            <Link to="/" className="hover:text-brand-yellow transition-colors">
              Forside
            </Link>
            <span>/</span>
            <span className="text-gray-500">Produktkatalog</span>
            <span>/</span>
            <span className="text-brand-yellow">Liftudlejning</span>
          </nav>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Tekstkolonne */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/20 px-3.5 py-1.5 text-xs font-black uppercase tracking-wider text-brand-yellow mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Sikkerhed & Fleksibilitet i Højden</span>
              </div>

              <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-5xl leading-[1.1]">
                Liftudlejning, <br className="hidden sm:inline" />
                <span className="text-brand-yellow">der passer til din opgave i højden</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed font-normal">
                Hos <Link to="/" className="text-gray-900 font-semibold underline decoration-brand-yellow decoration-2 underline-offset-4 hover:text-brand-yellow transition-colors">Vesthimmerlands Materieludlejning</Link> kan du leje en lift til arbejde i højden. Vi udlejer blandt andet sakselifte og bomlifte til byggeri, montage og facadearbejde. Fortæl os, hvor og hvordan du skal arbejde, så hjælper vi dig med at vælge en lift, der passer til opgaven.
              </p>

              {/* Handlingsknapper */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-sm hover:bg-brand-yellow-hover transition-all duration-200 cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href={catalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white border border-brand-grey px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-gray-800 shadow-sm hover:border-brand-yellow hover:text-brand-yellow transition-all duration-200"
                >
                  <span>Se lifte i online-katalog</span>
                  <ExternalLink className="h-4 w-4" />
                </a>

                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-brand-yellow px-2 py-2 transition-colors"
                >
                  <Phone className="h-4 w-4 text-brand-yellow" />
                  <span>{COMPANY_INFO.phone}</span>
                </a>
              </div>

              {/* USP Highlights */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-8 border-t border-brand-grey/60">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Godkendt & sikkerhedstestet</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Bomlifte & Sakselifte</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Egne mekanikere</span>
                </div>
              </div>
            </div>

            {/* Billedekolonne */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                <div className="overflow-hidden rounded-2xl border border-brand-grey bg-white shadow-xl aspect-[4/3]">
                  <img
                    src={getOptimizedUrl(heroImage, 1000)}
                    alt="JLG bomlift til sikker personløft og montagearbejde i højden hos Vesthimmerlands Materieludlejning"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>

                {/* Svævende info-badge */}
                <div className="absolute -bottom-6 -left-4 sm:bottom-4 sm:left-4 rounded-xl bg-white/95 backdrop-blur-sm border border-brand-grey p-4 shadow-lg flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow text-white">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-wider text-gray-900">
                      Godkendte Lifte
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      Klar til brug med løbende mekanikereftersyn
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Hvilken lift passer til arbejdet? */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Opgavebaseret Rådgivning
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvilken lift passer til arbejdet?
            </h2>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7 space-y-6 text-base text-gray-700 leading-relaxed">
              <p>
                Det gør en forskel, om du skal arbejde lige over dig selv eller nå hen til et sted, hvor liften ikke kan stå tæt på. Vi tager udgangspunkt i opgaven, adgangsforholdene og det sted, liften skal placeres. Det giver et bedre grundlag for at vælge materiel end arbejdshøjden alene.
              </p>

              {/* De 3 vurderingspunkter i SEO-teksten */}
              <div className="grid grid-cols-1 gap-4 pt-2">
                <div className="rounded-xl border border-brand-grey bg-brand-light p-5 flex items-start gap-4 transition-all duration-200 hover:border-brand-yellow/30 hover:bg-white hover:shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow shrink-0">
                    <ArrowUpFromLine className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Arbejdshøjde og sideudlæg
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Hvor højt du skal arbejde, og om du også skal kunne nå ud til siden.
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-light p-5 flex items-start gap-4 transition-all duration-200 hover:border-brand-yellow/30 hover:bg-white hover:shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow shrink-0">
                    <Compass className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Placering og fremkommelighed
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Hvor liften skal stå, og hvor meget plads der er til at komme frem.
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-light p-5 flex items-start gap-4 transition-all duration-200 hover:border-brand-yellow/30 hover:bg-white hover:shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow shrink-0">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                      Arbejdsmiljø og underlag
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Om arbejdet foregår inde eller ude, og hvordan underlaget er.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hurtig overbliksboks til højre */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border-2 border-brand-yellow/20 bg-brand-light p-6 sm:p-8">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow mb-2">
                  Kompetent Vejledning
                </h3>
                <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Vi hjælper dig i mål i højden
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Uanset om du skal male en facade, opsætte belysning i en lagerhal eller montere stålkonstruktioner, finder vi den rette lifttype til dit miljø.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg bg-white p-3.5 border border-brand-grey">
                    <div className="rounded-md bg-brand-yellow/10 p-2 text-brand-yellow shrink-0">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase text-gray-900">
                        Indendørs opgaver
                      </span>
                      <span className="text-xs text-gray-600">
                        Eldrevne sakselifte med afsmitningsfrie dæk til fabrikshaller og butikker.
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-lg bg-white p-3.5 border border-brand-grey">
                    <div className="rounded-md bg-brand-yellow/10 p-2 text-brand-yellow shrink-0">
                      <Maximize2 className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase text-gray-900">
                        Udendørs & Facader
                      </span>
                      <span className="text-xs text-gray-600">
                        Diesel- eller hybriddrevne bomlifte med 4x4 træk til ujævnt terræn.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-brand-grey flex items-center justify-between">
                  <a
                    href="#kontakt-tilbud"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                  >
                    <span>Få rådgivning nu</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>

                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900"
                  >
                    <Phone className="h-3 w-3 text-brand-yellow" />
                    <span>{COMPANY_INFO.phone}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 2: Sakselift vs. Bomlift (+ Minikraner) */}
      <section className="bg-brand-dark py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Lifttyper & Egenskaber
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Vælg den rigtige lifttype til opgaven
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Forskellige arbejdsopgaver kræver forskellig mobilitet og rækkevidde. Herunder kan du se fordelene ved sakselifte og bomlifte.
            </p>
          </div>

          {/* 2 Primære Lifttyper i store præsentationskort */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Sakselift Kort */}
            <div className="flex flex-col justify-between rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 shadow-sm hover:border-brand-yellow/40 transition-all duration-300">
              <div>
                <div className="overflow-hidden rounded-xl border border-brand-grey aspect-16/10 mb-6">
                  <img
                    src={getOptimizedUrl(scissorImage, 800)}
                    alt="Elektrisk sakselift til sikkert lodret montagearbejde indendørs og udendørs"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                  />
                </div>

                <div className="inline-flex items-center gap-1.5 rounded-md bg-brand-yellow/10 px-2.5 py-1 text-xs font-bold text-brand-yellow uppercase tracking-wider mb-3">
                  <ArrowUpFromLine className="h-3.5 w-3.5" />
                  <span>Lodret Løft</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Sakselift, når du skal arbejde lige op
                </h3>

                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    En sakselift hæver arbejdsplatformen lodret og kan være et godt valg, når du skal udføre montage eller andet arbejde over et afgrænset område. Det kan for eksempel være en opgave inde i en bygning. Vi hjælper med at vurdere, om pladsen og underlaget passer til den lift, du har brug for.
                  </p>
                </div>

                <ul className="mt-6 space-y-2.5 pt-4 border-t border-gray-100 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Stor arbejdsplatform med plads til værktøj og montører</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Ideel til installation, kabeltræk og loftsmontering</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Kompakte mål gør kørsel gennem døre mulig</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                <a
                  href={sakselifteCatalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                >
                  <span>Se sakselifte i katalog</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>

                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900"
                >
                  <span>Forespørg leje</span>
                  <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>

            {/* Bomlift Kort */}
            <div className="flex flex-col justify-between rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 shadow-sm hover:border-brand-yellow/40 transition-all duration-300">
              <div>
                <div className="overflow-hidden rounded-xl border border-brand-grey aspect-16/10 mb-6">
                  <img
                    src={getOptimizedUrl(boomImage, 800)}
                    alt="Terrængående bomlift med stor rækkevidde og knækarm til facadearbejde"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                  />
                </div>

                <div className="inline-flex items-center gap-1.5 rounded-md bg-brand-yellow/10 px-2.5 py-1 text-xs font-bold text-brand-yellow uppercase tracking-wider mb-3">
                  <Maximize2 className="h-3.5 w-3.5" />
                  <span>Stort Sideudlæg</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Bomlift, når du skal nå længere ud
                </h3>

                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    En bomlift kan være relevant, når arbejdsstedet ikke ligger lige over liften. Den kan give adgang til dele af en facade eller et område, hvor liften ikke kan placeres helt tæt på. Vi spørger ind til rækkevidde og omgivelser, så du ikke vælger ud fra højden alene.
                  </p>
                </div>

                <ul className="mt-6 space-y-2.5 pt-4 border-t border-gray-100 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Fleksibel knækarm eller teleskopbom over forhindringer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Perfekt til facaderenovering, malerarbejde og tagrender</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                    <span>Fås med 4WD til kørsel på byggepladser og græsplæner</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                <a
                  href={bomlifteCatalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                >
                  <span>Se bomlifte i katalog</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>

                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900"
                >
                  <span>Forespørg leje</span>
                  <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>

          {/* Relateret Materiel: Minikraner & Minilæssere */}
          <div className="mt-12 space-y-6">
            <div className="max-w-2xl">
              <span className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow">
                Kombiner dit materiel
              </span>
              <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900 mt-1">
                Relateret materiel til byggepladsen
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Minikraner Kort */}
              <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 flex flex-col justify-between shadow-sm hover:border-brand-yellow/30 transition-all">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/10 px-3 py-1 text-xs font-bold text-brand-yellow uppercase tracking-wider mb-3">
                    <HardHat className="h-3.5 w-3.5" />
                    <span>Tunge løft i trange rum</span>
                  </div>
                  <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-2">
                    Minikraner til tunge præcisionsløft
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed mb-6">
                    Skal du have materialer, glas eller stålspær løftet på plads frem for at arbejde fra en platform? Vi tilbyder udlejning af kompakte minikraner til de mest krævende løfteopgaver.
                  </p>
                </div>

                <div>
                  <div className="overflow-hidden rounded-xl border border-brand-grey aspect-16/9 mb-5">
                    <img
                      src={getOptimizedUrl(craneImage, 600)}
                      alt="Kompakt minikran til tunge præcisionsløft og glasmontering på trange byggepladser"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to="/minikran"
                      className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                    >
                      <span>Læs mere om minikran</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <a
                      href={kranerCatalogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md bg-white border border-brand-grey px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-800 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                    >
                      <span>Katalog</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Minilæssere Kort */}
              <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 flex flex-col justify-between shadow-sm hover:border-brand-yellow/30 transition-all">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/10 px-3 py-1 text-xs font-bold text-brand-yellow uppercase tracking-wider mb-3">
                    <Truck className="h-3.5 w-3.5" />
                    <span>Flytning af jord & materialer</span>
                  </div>
                  <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-2">
                    Minilæssere til jord og byggematerialer
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed mb-6">
                    Skal du flytte jord, grus, paller eller andet materiale rundt på arbejdsstedet? En kompakt minilæsser er den perfekte makker på byggepladsen, hvor pladsen er trang.
                  </p>
                </div>

                <div>
                  <div className="overflow-hidden rounded-xl border border-brand-grey aspect-16/9 mb-5">
                    <img
                      src={getOptimizedUrl(minilaesserImage, 600)}
                      alt="Kompakt minilæsser til hurtig transport af jord og byggematerialer"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to="/minilaesser"
                      className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                    >
                      <span>Læs mere om minilæsser</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <Link
                      to="/maskinudlejning"
                      className="inline-flex items-center gap-1.5 rounded-md bg-white border border-brand-grey px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-800 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                    >
                      <span>Se maskiner</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Hvad koster liftudlejning? */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Pris & Gennemskuelighed
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvad koster liftudlejning?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Tekstblok med SEO-afsnit */}
            <div className="lg:col-span-7 flex flex-col justify-between rounded-2xl border border-brand-grey bg-brand-light p-6 sm:p-8">
              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Mange søger på ordene liftudlejning pris, men der er ikke én pris, som passer til alle opgaver. Prisen afhænger blandt andet af lifttype, hvor længe du skal bruge den, og forholdene på arbejdsstedet. Derfor giver vi dig hellere et tilbud, der tager udgangspunkt i det, du skal have løst.
                </p>
                <p>
                  Fortæl os gerne, om du arbejder inde eller ude, og om du skal kunne nå ind over noget. Det er også værd at få afklaret, hvad der er med i lejeaftalen, herunder eventuel transport. Så kan du tage stilling til den samlede løsning i stedet for kun at se på en dagspris.
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-grey flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                    Gennemskuelige lejeaftaler
                  </span>
                  <span className="text-sm font-semibold text-gray-800">
                    Ingen ubehagelige overraskelser – vi aftaler pris og transport på forhånd.
                  </span>
                </div>
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors shrink-0"
                >
                  <span>Indhent tilbud</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* 3 Prisfaktorer */}
            <div className="lg:col-span-5 flex flex-col justify-between gap-4">
              <div className="rounded-xl border border-brand-grey bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    A
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Lifttype & Arbejdshøjde
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Kompakt elektrisk sakselift vs. stor terrængående bomlift med stort sideudlæg.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-grey bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    B
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Lejeperiode (Dags- eller langtidsleje)
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Fordelagtige vilkår ved samlet ugeleje, månedsleje eller faste entrepriseaftaler.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-grey bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow font-bold text-xs shrink-0">
                    C
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase text-gray-900">
                      Levering & Transport
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Hurtig levering og afhentning direkte på byggepladsen med egne lastbiler.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-brand-yellow/30 bg-brand-yellow/5 p-4 text-center">
                <span className="text-xs font-bold text-brand-yellow">
                  Fuld gennemskuelighed på samlet aftale og forsikringsforhold.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Hvad skal vi vide for at give dig en pris? */}
      <section className="bg-brand-dark py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Hurtig Prisforespørgsel
            </span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvad skal vi vide for at give dig en pris?
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Du behøver ikke kende den præcise liftmodel, før du kontakter os. En kort beskrivelse af opgaven er nok til at starte samtalen. Jo mere vi ved om arbejdsstedet og tidsplanen, desto bedre kan vi rådgive om valget af lift og give dig et uforpligtende tilbud på lejen.
            </p>
          </div>

          {/* 3 punkter i overskueligt tjeklistegrid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Punkt 1 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  01
                </div>
                <ArrowUpFromLine className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Opgavens karakter
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Beskriv, hvad du skal nå, og hvilket arbejde du skal udføre.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Angiv cirka arbejdshøjde samt om du skal række ind over et tag eller hegn.
              </div>
            </div>

            {/* Punkt 2 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  02
                </div>
                <Compass className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Arbejdssted & adgang
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Oplys, hvor arbejdet foregår, og om pladsen er trang.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Fortæl om underlaget er fast beton, asfalt eller blødt græs/jord.
              </div>
            </div>

            {/* Punkt 3 */}
            <div className="relative rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 shadow-sm hover:border-brand-yellow/40 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow font-black text-lg">
                  03
                </div>
                <Clock className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-2">
                Tidsplan & periode
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fortæl, hvornår du skal bruge liften, og hvor længe opgaven varer.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                Tip: Vi reserverer materiellet i god tid, så du er garanteret levering.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 5: Liftudlejning i Aalborg og Nordjylland */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="rounded-3xl border border-brand-grey bg-brand-light p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Lokal Forankring
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                  Liftudlejning i <br />
                  <span className="text-brand-yellow">Aalborg og Nordjylland</span>
                </h2>

                <div className="mt-6 space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    Vi tilbyder liftudlejning i Nordjylland fra vores afdelinger i Svenstrup ved Aalborg og Løgstør. Søger du liftudlejning i Aalborg, hjælper vi dig med at afklare valg af lift. Har du en opgave i Aarhus, kan vi også hjælpe. Du kan også se vores <Link to="/maskinudlejning" className="text-brand-yellow font-semibold hover:underline">materieludlejning i Aalborg</Link>, hvis du har brug for andet udstyr til dit projekt.
                  </p>
                  <p>
                    Vi hjælper især håndværkere, entreprenører og virksomheder, der arbejder med byggeri og montage. Har du som privatperson en opgave i højden, er du velkommen til at spørge os om mulighederne. Vi taler gerne opgaven igennem, så du ved, hvilket materiel der kan være relevant.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3.5 py-1.5 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Svenstrup J (Aalborg)</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3.5 py-1.5 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Løgstør (Vesthimmerland)</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3.5 py-1.5 text-xs font-bold text-gray-800">
                    <MapPin className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Aarhus & Midtjylland</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-grey px-3.5 py-1.5 text-xs font-bold text-gray-800">
                    <Truck className="h-3.5 w-3.5 text-brand-yellow" />
                    <span>Hurtig levering med egne biler</span>
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3 text-gray-900 font-bold uppercase text-sm mb-2">
                    <MapPin className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span>Afdeling Svenstrup</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Jellingvej 19, 9230 Svenstrup J – hurtig adgang til hele Aalborg-området og E45.
                  </p>
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-yellow hover:underline"
                  >
                    <span>Ring til udlejningen: {COMPANY_INFO.phone}</span>
                  </a>
                </div>

                <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3 text-gray-900 font-bold uppercase text-sm mb-2">
                    <MapPin className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span>Afdeling Løgstør</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Stærk lokal forankring med hurtig servicering af hele Vesthimmerland og Limfjordsområdet.
                  </p>
                  <span className="text-xs font-semibold text-gray-500">
                    Samme høje service og rådgivning.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Skal vi finde liften til din opgave? (Rådgivning & Uforpligtende tilbudsformular) */}
      <section id="kontakt-tilbud" className="py-20 md:py-28 bg-white scroll-mt-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Venstre side: Rådgivning & Kontaktinfo */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Personlig Rådgivning
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                  Skal vi finde liften til din opgave?
                </h2>
              </div>

              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Vores egne mekanikere vedligeholder løbende materiellet, og liftene er godkendte og klar til brug. Det er værd at have på plads, når du planlægger arbejde i højden. Du skal stadig sikre dig, at arbejdsstedet er egnet, og at liften bruges efter anvisningerne og de regler, der gælder for opgaven.
                </p>
                <p>
                  Har du styr på opgaven, men er usikker på, hvilken lift du skal vælge? Skriv til os med oplysninger om arbejdssted, højde og lejeperiode. Så kan vi rådgive om mulighederne og give dig et uforpligtende tilbud. <Link to="/kontakt" className="text-brand-yellow font-semibold hover:underline">Kontakt os</Link>, så finder vi sammen en løsning, der passer til dit arbejde.
                </p>
                <p className="text-sm text-gray-600">
                  Ønsker du en fast aftale som virksomhed? Opret en konto nemt via vores <Link to="/bliv-kunde" className="text-brand-yellow font-semibold hover:underline">erhvervskundeoprettelse</Link>.
                </p>
              </div>

              {/* Direkte udlejere med billede og telefon */}
              <div className="pt-4 border-t border-brand-grey space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Tal direkte med vores udlejere
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {STAFF.map((member, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-brand-grey bg-brand-light p-4 flex items-center gap-3"
                    >
                      <img
                        src={getOptimizedUrl(member.image, 160)}
                        alt={`${member.name} - ${member.role} hos Vesthimmerlands Materieludlejning`}
                        className="h-12 w-12 rounded-full object-cover border border-brand-grey"
                      />
                      <div className="min-w-0">
                        <span className="block text-xs font-bold uppercase text-gray-900 truncate">
                          {member.name}
                        </span>
                        <span className="block text-[11px] text-gray-500 truncate">
                          {member.role}
                        </span>
                        <a
                          href={`tel:${member.phoneRaw}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-yellow hover:underline"
                        >
                          <Phone className="h-3 w-3" />
                          <span>{member.phone}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hovedkontor Info */}
              <div className="rounded-xl border border-brand-grey bg-brand-dark p-5 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-700">
                  <Phone className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Mail className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.email}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>{COMPANY_INFO.address}</span>
                </div>
              </div>
            </div>

            {/* Højre side: Formular */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 sm:p-8 md:p-10 shadow-sm">
                <div className="mb-6">
                  <h3 className="text-xl font-black uppercase tracking-tight text-gray-900">
                    Få et uforpligtende tilbud på liftudlejning
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Udfyld formularen med oplysninger om arbejdssted, arbejdshøjde og lejeperiode, så kontakter vi dig hurtigst muligt.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
